// Step 5 of the client-side X12 pipeline (997 variant): walks one
// transaction's flat body segments (from step 2's envelope tree) into the
// loop structure a human reader thinks in — AK1 (group being acknowledged),
// the AK2 loop per transaction set in that group (with its nested AK3
// segment-note loop and that AK3's nested AK4 element-note list), and AK9.
// Only ever runs against a transaction whose ST01 is "997"; anything else is
// left alone so it can still be shown, read-only, by the UI. Mirrors
// parser852.ts's pattern one-for-one, but against the 997's own loop shape.

import type {EdiSegment} from './tokenizer';
import type {EdiEnvelope, EdiGroup, EdiTransaction} from './envelope';

export interface Ak3Loop997 {
  ak3: EdiSegment;
  ak4: EdiSegment[];
}

export interface Ak2Loop997 {
  ak2: EdiSegment;
  ak3Loops: Ak3Loop997[];
  ak5?: EdiSegment;
}

export interface Parsed997 {
  ak1?: EdiSegment;
  ak2Loops: Ak2Loop997[];
  ak9?: EdiSegment;
}

/**
 * Walks one 997 transaction's flat body segments (ST/SE excluded) into its
 * loop structure. Never throws — a segment that doesn't fit anywhere
 * sensible in the current loop is simply not attached, rather than the walk
 * aborting; the validator is what reports on a document that's missing or
 * misordered.
 */
export function parse997Segments(segments: EdiSegment[]): Parsed997 {
  const ak2Loops: Ak2Loop997[] = [];
  let ak1: EdiSegment | undefined;
  let ak9: EdiSegment | undefined;
  let currentAk2Loop: Ak2Loop997 | undefined;
  let currentAk3Loop: Ak3Loop997 | undefined;

  for (const s of segments) {
    switch (s.id) {
      case 'AK1':
        ak1 = s;
        break;

      case 'AK2':
        currentAk2Loop = {ak2: s, ak3Loops: []};
        ak2Loops.push(currentAk2Loop);
        currentAk3Loop = undefined;
        break;

      case 'AK3':
        if (currentAk2Loop) {
          currentAk3Loop = {ak3: s, ak4: []};
          currentAk2Loop.ak3Loops.push(currentAk3Loop);
        }
        break;

      case 'AK4':
        if (currentAk3Loop) {
          currentAk3Loop.ak4.push(s);
        }
        break;

      case 'AK5':
        if (currentAk2Loop) {
          currentAk2Loop.ak5 = s;
        }
        currentAk3Loop = undefined;
        break;

      case 'AK9':
        ak9 = s;
        currentAk2Loop = undefined;
        currentAk3Loop = undefined;
        break;

      default:
        break;
    }
  }

  return {ak1, ak2Loops, ak9};
}

export interface EdiTransactionWithParsed997 extends EdiTransaction {
  /** Left undefined for a transaction whose ST01 isn't "997" — it keeps only
   * its raw segment list, for read-only display. */
  parsed997?: Parsed997;
}

export interface EdiGroupWithParsed997 extends Omit<EdiGroup, 'transactions'> {
  transactions: EdiTransactionWithParsed997[];
}

export interface EdiEnvelopeWithParsed997 extends Omit<EdiEnvelope, 'groups'> {
  groups: EdiGroupWithParsed997[];
}

/**
 * Parses every 997 transaction in an envelope tree (step 2's output),
 * attaching `parsed997` to each. A non-997 transaction is passed through
 * unchanged, `parsed997` left undefined.
 */
export function attachParsed997(
  envelope: EdiEnvelope
): EdiEnvelopeWithParsed997 {
  return {
    ...envelope,
    groups: envelope.groups.map(group => ({
      ...group,
      transactions: group.transactions.map(transaction => ({
        ...transaction,
        parsed997:
          transaction.transactionSetCode === '997'
            ? parse997Segments(transaction.segments)
            : undefined,
      })),
    })),
  };
}
